import { dateMs } from './calc';
import type { DateValue, FileValue, OptionColor, PropertyDef, PropertyType, SelectOption } from './db-types';
import { OPTION_COLORS } from './db-types';
import { newId } from './ids';
import { parseNumber } from './number-format';

const pad = (n: number) => String(n).padStart(2, '0');

/** Picks a colour for a new option (random, as in Notion; deterministic when `seed` is given). */
export function randomOptionColor(seed?: number): OptionColor {
  const colors = OPTION_COLORS.filter((c) => c !== 'default');
  const i = seed === undefined ? Math.floor(Math.random() * colors.length) : Math.abs(seed) % colors.length;
  return colors[i]!;
}

/** Text rendering of a raw stored value of a given property (used when changing types). */
export function rawToText(prop: PropertyDef, raw: unknown, memberName: (id: string) => string = (id) => id): string {
  if (raw === null || raw === undefined) return '';
  switch (prop.type) {
    case 'select':
    case 'status':
      return prop.options?.find((o) => o.id === raw)?.name ?? '';
    case 'multi_select':
      return (raw as string[]).map((id) => prop.options?.find((o) => o.id === id)?.name ?? '').filter(Boolean).join(', ');
    case 'person':
      return (raw as string[]).map(memberName).join(', ');
    case 'checkbox':
      return raw ? 'Yes' : 'No';
    case 'date': {
      const d = raw as DateValue;
      return `${d.start}${d.time ? ` ${d.time}` : ''}${d.end ? ` → ${d.end}` : ''}`;
    }
    case 'files':
      return (raw as FileValue[]).map((f) => f.url).join(', ');
    case 'number':
      return String(raw);
    default:
      return typeof raw === 'string' ? raw : Array.isArray(raw) ? raw.join(', ') : String(raw);
  }
}

function parseDateText(s: string): DateValue | null {
  const t = dateMs(s.trim());
  if (t === null) return null;
  const d = new Date(t);
  const hasTime = /\d{1,2}:\d{2}/.test(s);
  return { start: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`, ...(hasTime ? { time: `${pad(d.getHours())}:${pad(d.getMinutes())}` } : {}) };
}

export interface ConvertResult {
  /** New raw values by row id (undefined = clear). */
  values: Record<string, unknown>;
  /** Options for select-like targets, including ones created from existing text. */
  options?: SelectOption[];
}

const SELECTISH: PropertyType[] = ['select', 'multi_select', 'status'];

/**
 * Converts every row's value when a property changes type. Text becomes options (comma-separated
 * for multi-select), options become text, numbers become text and back, checkboxes become Yes/No,
 * dates become ISO text, and computed types (formula, rollup, …) start empty.
 */
export function convertProperty(prop: PropertyDef, rawByRow: Record<string, unknown>, to: PropertyType, memberName?: (id: string) => string): ConvertResult {
  const values: Record<string, unknown> = {};
  if (to === prop.type) return { values: { ...rawByRow } };
  const options: SelectOption[] = SELECTISH.includes(to) && SELECTISH.includes(prop.type) ? [...(prop.options ?? [])] : [];
  const optionFor = (name: string) => {
    const n = name.trim();
    if (!n) return null;
    let o = options.find((x) => x.name.toLowerCase() === n.toLowerCase());
    if (!o) {
      o = { id: newId(), name: n, color: randomOptionColor(options.length) };
      options.push(o);
    }
    return o.id;
  };
  for (const [rowId, raw] of Object.entries(rawByRow)) {
    if (raw === null || raw === undefined) continue;
    // Between select-like types, keep option ids.
    if (SELECTISH.includes(prop.type) && SELECTISH.includes(to)) {
      const ids = Array.isArray(raw) ? (raw as string[]) : [raw as string];
      values[rowId] = to === 'multi_select' ? ids : ids[0];
      continue;
    }
    const text = rawToText(prop, raw, memberName);
    switch (to) {
      case 'title':
      case 'text':
      case 'url':
      case 'email':
      case 'phone':
        if (text) values[rowId] = text;
        break;
      case 'number': {
        const n = typeof raw === 'number' ? raw : typeof raw === 'boolean' ? (raw ? 1 : 0) : parseNumber(text);
        if (n !== null) values[rowId] = n;
        break;
      }
      case 'select':
      case 'status': {
        const id = optionFor(text.split(',')[0] ?? '');
        if (id) values[rowId] = id;
        break;
      }
      case 'multi_select': {
        const ids = text.split(',').map(optionFor).filter((x): x is string => !!x);
        if (ids.length) values[rowId] = [...new Set(ids)];
        break;
      }
      case 'checkbox':
        values[rowId] = typeof raw === 'boolean' ? raw : typeof raw === 'number' ? raw !== 0 : /^(yes|true|1|checked|x|✓)$/i.test(text.trim());
        break;
      case 'date': {
        const d = prop.type === 'created_time' || prop.type === 'last_edited_time' ? parseDateText(new Date(raw as number).toISOString()) : parseDateText(text);
        if (d) values[rowId] = d;
        break;
      }
      case 'person':
        if (prop.type === 'created_by' || prop.type === 'last_edited_by') values[rowId] = [raw];
        break;
      case 'files':
        if (/^https?:\/\//.test(text)) values[rowId] = text.split(',').map((u) => ({ name: u.trim().split('/').pop() ?? u.trim(), url: u.trim() }));
        break;
      default:
        break;
    }
  }
  return SELECTISH.includes(to) ? { values, options } : { values };
}

export interface InputResult {
  ok: boolean;
  value: unknown;
}

/**
 * Validates and normalises typed input. Numbers must parse (otherwise rejected, keeping the old
 * value). URL, email and phone keep whatever was typed, as Notion does; links are only made for
 * values that look valid (see `linkFor`).
 */
export function normalizeInput(type: PropertyType, input: string): InputResult {
  const s = input.trim();
  if (type === 'number') {
    if (!s) return { ok: true, value: null };
    const n = parseNumber(s);
    return n === null ? { ok: false, value: null } : { ok: true, value: n };
  }
  if (type === 'url' || type === 'email' || type === 'phone') return { ok: true, value: s || null };
  return { ok: true, value: input };
}

export function isValidEmail(s: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

export function isValidPhone(s: string): boolean {
  return /^\+?[\d\s().-]{3,}$/.test(s) && /\d/.test(s);
}

/** The href for a URL, email or phone value: https:// added to bare domains, mailto:, tel:. */
export function linkFor(type: PropertyType, value: string): string | null {
  const s = value.trim();
  if (!s) return null;
  if (type === 'url') return /^[a-z][\w+.-]*:/i.test(s) ? s : /^[\w-]+(\.[\w-]+)+/.test(s) ? `https://${s}` : null;
  if (type === 'email') return isValidEmail(s) ? `mailto:${s}` : null;
  if (type === 'phone') return isValidPhone(s) ? `tel:${s.replace(/[^\d+]/g, '')}` : null;
  return null;
}
